import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import { idlePin } from './types';
import type { IdleAlert } from './types';

/**
 * An idle event in the alerts feed (slice 66k) — the NINTH item shape. The engine
 * was left running while the vehicle stayed parked past the tenant's idle threshold.
 * Severity `info`, exactly like an ignition event: a routine operational fact a fleet
 * manager opted in to see, not an incident, so the row is QUIET throughout — the same
 * restraint `IgnitionRow` shows, NEVER attention weight. There is no critical styling
 * here at any point; the pin below does not change that.
 *
 * 🔴 IT MAY CARRY A LOCATION, and that decides the affordance, exactly like
 * `IgnitionRow`. The wire carries the crossing fix's own valid coords as nullable
 * `observedLat`/`observedLon` (both set, or both null — never invented from live
 * state; ADR 0011 decision 2, position 27). Read ONLY through `idlePin`. So:
 *   - WITH a pin → a select button (like `OverspeedRow`): selecting it centres the
 *     fleet map on the last-known fix (and marks read). On vehicle detail (no map)
 *     selecting just marks it read.
 *   - WITHOUT a pin → a `Link` straight to the vehicle, the honest destination for a
 *     location-less event.
 * The pin is LABELLED "last known location" so it is never read as the exact spot the
 * vehicle idled. Because idle stays QUIET, there is no "no location" line when
 * unpinned — the plain Link is the whole affordance.
 *
 * The duration (`idleMinutes`, the frozen detail — ≈threshold while the episode is
 * open, the final duration once it closed) is rendered as "idling for N min" beside
 * the observed time, mirroring the app's idle row (`alert_row_view.dart`). Idle stays
 * OFF by default per tenant and only reaches the feed when an owner/manager turns the
 * idle switch on in Settings (the industry-standard opt-in, PLAN). Tokens only; no hex
 * literal enters this file.
 */
export function IdleRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: IdleAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: IdleAlert) => void;
  t: TFunction;
}) {
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const pin = idlePin(alert);

  // Quiet throughout: an info event never carries attention weight — with a pin or
  // without one. The unread dot is slate (not the accent an attention row uses), and
  // the title stays medium. A selected row gets the primary tint every positioned
  // row uses, but that is selection, not severity. (Mirrors IgnitionRow.)
  const titleClass = 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : 'bg-raad-slate';

  const containerClass = [
    'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
    selected
      ? 'border-raad-primary/40 bg-raad-primary/10'
      : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
  ].join(' ');

  // Shared inner content for both wrappers (button when positioned, Link otherwise).
  const inner = (
    <div className="flex items-start gap-2.5">
      <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={['truncate text-sm', titleClass].join(' ')}>{t('alerts.idle.title')}</span>
          <span
            data-testid="idle-tag"
            className="flex-none rounded-md bg-raad-slate/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-slate"
          >
            {t('alerts.idle.tag')}
          </span>
          {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
        </div>

        {plate ? (
          <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
        ) : null}

        {/* Observed time and the frozen idle duration, mirroring the app's
            "<time> · idling for N min" line. N is the top-level `idleMinutes`. */}
        <div className="mt-1 text-[12px] text-raad-muted">
          {t('alerts.observedAt', { time: observedTime })}
          {' · '}
          {t('alerts.idle.for', { minutes: alert.idleMinutes })}
        </div>

        {/* The last-known location line, present ONLY when there is a pin. Quiet
            (muted) so an idle event never draws the eye; the label states, honestly,
            that this is the last known location, never the exact spot it idled. */}
        {pin ? (
          <div
            data-testid="idle-last-known"
            className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-raad-muted"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11Z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {t('alerts.idle.lastKnownLocation')}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <li>
      {pin ? (
        // Positioned: selecting centres the fleet map on the last-known fix (and
        // marks read), exactly like OverspeedRow / IgnitionRow / PowerRow / TamperRow.
        <button
          type="button"
          onClick={() => onSelect(alert)}
          aria-pressed={selected}
          data-testid="idle-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="true"
          className={containerClass}
        >
          {inner}
        </button>
      ) : (
        // Location-less: a Link to the vehicle — the honest destination — never a
        // map-select button that would centre on nothing.
        <Link
          to={`/vehicles/${alert.vehicleId}`}
          data-testid="idle-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="false"
          className={containerClass}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}
